export interface HostPathConfig {
  host: string;
  path: string;
}

export interface LoginEnvConfig {
  entry: string;
  redirectAfterLogout: string;
}

export interface SmartyStreetConfig {
  hostedKey: number;
}

export interface ApiConfig {
  host: string;
  path: string;
  path_v2: string;
  userService: string;
  pricingService: string;
  bulkService: string;
  salesforce: string;
}

export interface ServerConfig {
  host: string;
  path: string;
}

export interface MyCargillConfig {
  host: string;
}

export interface OktaOIDCEnvConfig {
  baseUrl: string;
  issuer: string;
  issuerPath: string;
  clientId: string;
}

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  forwardErrorsToLogs: boolean;
  datacenter: string;
  samplerate: number;
}

export interface EnvironmentConfig {
  apiInfo: HostPathConfig;
  login: LoginEnvConfig;
  strapiConfig: HostPathConfig;
  smartyStreet: SmartyStreetConfig;
  api: ApiConfig;
  server: ServerConfig;
  myCargill: MyCargillConfig;
  oktaOIDC: OktaOIDCEnvConfig;
  datadogConfig: DatadogConfig;
  userService: HostPathConfig;
  reportingService: HostPathConfig;
}

export interface Environment extends EnvironmentConfig {
  env: string;
}

type EnvKey = 'dev' | 'stage' | 'prod';

const dev: EnvironmentConfig = {
  apiInfo: {
    host: 'https://api-dev.dev.dev-cglcloud.com/api/dxo/onboarding-service',
    path: '/v1'
  },
  login: {
    entry: '/login',
    redirectAfterLogout: '/login'
  },
  strapiConfig: {
    host: 'https://api-dev.dev.dev-cglcloud.com',
    path: '/api/cfb-strapi/v1'
  },
  smartyStreet: {
    hostedKey: 6179657295308775
  },
  api: {
    host: 'https://api-dev.dev.dev-cglcloud.com',
    path: '/api/dxo/fibi/v1',
    path_v2: '/api/dxo/fibi',
    userService: '/api/dxo/fibi/user-services/v1',
    pricingService: '/api/dxo/fibi/commodity-pricing-service/v1',
    bulkService: '/api/dxo/v1',
    salesforce: '/api/dxo/fibi/salesforce'
  },
  server: {
    host: 'https://redcarpetonboarding.dev.cglcloud.in',
    path: '/food/onboarding'
  },
  myCargill: {
    host: 'https://mycargill.dev.cglcloud.in'
  },
  oktaOIDC: {
    baseUrl: 'https://login-qa-customer.cargill.com',
    issuer: 'https://login-qa-customer.cargill.com/oauth2/auszzmfs36uUBqxYU0h7',
    issuerPath: '/oauth2/auszzmfs36uUBqxYU0h7',
    clientId: '0oaq6f5qr0yyZB6Oc0h7'
  },
  datadogConfig: {
    applicationId: 'aa9aa74a-0981-4268-9c50-0c2ea4e5730b',
    clientToken: 'pubd4fac1725175be41c3d37ab108abf271',
    forwardErrorsToLogs: true,
    datacenter: 'us',
    samplerate: 100
  },
  userService: {
    host: 'https://api-dev.dev.dev-cglcloud.com/api/dxo/onboarding/user-service',
    path: '/v1'
  },
  reportingService: {
    host: 'https://api-dev.dev.dev-cglcloud.com/api/dxo/onboarding/reporting-service',
    path: '/v1'
  }
};

const stage: EnvironmentConfig = {
  apiInfo: {
    host: 'https://api-stage.stage.cglcloud.in/api/dxo/onboarding-service',
    path: '/v1'
  },
  server: {
    host: 'https://onboarding.stage.cglcloud.in',
    path: '/food/onboarding'
  },
  smartyStreet: {
    hostedKey: 6179657295308775
  },
  strapiConfig: {
    host: 'https://api-stage.stage.cglcloud.in',
    path: '/api/cfb-strapi/v1'
  },
  api: {
    host: 'https://api-stage.stage.cglcloud.in',
    path: '/api/dxo/fibi/v1',
    path_v2: '/api/dxo/fibi',
    userService: '/api/dxo/fibi/user-services/v1',
    pricingService: '/api/dxo/fibi/commodity-pricing-service/v1',
    bulkService: '/api/dxo/v1',
    salesforce: '/api/dxo/fibi/salesforce'
  },
  myCargill: {
    host: 'https://stage.mycargill.com'
  },
  login: {
    entry: '/login',
    redirectAfterLogout: '/login'
  },
  oktaOIDC: {
    baseUrl: 'https://login-stage.mycargill.com',
    issuer: 'https://login-stage.mycargill.com/oauth2/aus111li82blhgcDe0h8',
    issuerPath: '/oauth2/aus111li82blhgcDe0h8',
    clientId: '0oaqekxczpVrUvW0e0h7'
  },
  datadogConfig: {
    applicationId: 'aa9aa74a-0981-4268-9c50-0c2ea4e5730b',
    clientToken: 'pubd4fac1725175be41c3d37ab108abf271',
    forwardErrorsToLogs: true,
    datacenter: 'us',
    samplerate: 100
  },
  userService: {
    host: 'https://api-stage.stage.cglcloud.in/api/dxo/onboarding/user-service',
    path: '/v1'
  },
  reportingService: {
    host: 'https://api-stage.stage.cglcloud.in/api/dxo/onboarding/reporting-service',
    path: '/v1'
  }
};

const prod: EnvironmentConfig = {
  apiInfo: {
    host: 'https://api.cglcloud.com/api/dxo/onboarding-service',
    path: '/v1'
  },
  server: {
    host: 'https://onboarding.mycargill.com',
    path: '/food/onboarding'
  },
  smartyStreet: {
    hostedKey: 6179658537714943
  },
  strapiConfig: {
    host: 'https://api.cglcloud.com',
    path: '/api/cfb-strapi/v1'
  },
  api: {
    host: 'https://api.cglcloud.com',
    path: '/api/dxo/fibi/v1',
    path_v2: '/api/dxo/fibi',
    userService: '/api/dxo/fibi/user-services/v1',
    pricingService: '/api/dxo/fibi/commodity-pricing-service/v1',
    bulkService: '/api/dxo/v1',
    salesforce: '/api/dxo/fibi/salesforce'
  },
  login: {
    entry: '/login',
    redirectAfterLogout: '/login'
  },
  myCargill: {
    host: 'https://mycargill.com'
  },
  oktaOIDC: {
    baseUrl: 'https://login.mycargill.com',
    issuer: 'https://login.mycargill.com/oauth2/aus7560izdyHixyCz0i7',
    issuerPath: '/oauth2/aus7560izdyHixyCz0i7',
    clientId: '0oa3uh6txmp1ajohL0i7'
  },
  datadogConfig: {
    applicationId: 'fd40088a-da29-42dd-a34f-903ac6730759',
    clientToken: 'pub0a3b80da9de6f14e7fab69e3b9aeaecd',
    forwardErrorsToLogs: true,
    datacenter: 'us',
    samplerate: 100
  },
  userService: {
    host: 'https://api.cglcloud.com/api/dxo/onboarding/user-service',
    path: '/v1'
  },
  reportingService: {
    host: 'https://api.cglcloud.com/api/dxo/onboarding/reporting-service',
    path: '/v1'
  }
};

const localConfig: Record<EnvKey, EnvironmentConfig> = {
  dev,
  stage,
  prod
};

export const environment = (() => {
  let response: Environment | undefined;
  return (env?: string): Environment => {
    if (!response) {
      const hasConfig = !!env && Object.prototype.hasOwnProperty.call(localConfig, env);
      const resolvedEnvKey: EnvKey = hasConfig ? (env as EnvKey) : 'dev';
      const config = localConfig[resolvedEnvKey];

      response = { ...config, env: env || resolvedEnvKey };
      if (typeof window !== 'undefined') {
        window.__APP_CONFIG__ = response;
      }
    }
    return response;
  };
})();
